import * as R from 'ramda';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';

import CropPreview from './CropPreview';
import { Crop } from './types';
import { restrictCrop, useImageSize } from './utils';

export type Props = {
  className?: string;
  crop: Crop;
  imageUrl: string;
  maxZoom: number;
  onCropChange: (crop: Crop) => void;
};

// The Handle interface is exposed through ref
type Handle = {
  /**
   * Use this to alter the zoom level. This should be used rather than directly
   * changing the zoom property on the Crop object to ensure that the focus
   * point stays at the center of the crop area.
   */
  zoom: (zoom: number) => void;
};

type Point = { x: number; y: number };

const WHEEL_SENSITIVITY = 0.01;

const Preview = styled(CropPreview)`
  cursor: move;
  user-select: none;
`;

const preventDefault = (event: Event) => {
  event.preventDefault();
};

/**
 * Change the crop area size while keeping the pivot point stationary in the resulting cropped image.
 *
 * @imageAspect The aspect ratio of the image (width divided by height)
 * @pivot A coordinate in the cropped image that we want to keep stationary while zooming. Between (0,0) and (1,1) where the former is the top-left corner and latter is the bottom-right corner.
 * @zoom The desired zoom level
 * @crop The original cropping whose zoom level we want to adjust
 */
const zoomWithPivot = (imageAspect: number, pivot: { x: number; y: number }, zoom: number, crop: Crop) => {
  const imageWidth = imageAspect > crop.aspect ? imageAspect / crop.aspect : 1;
  const oldCropWidth = 1 / crop.zoom;
  const cropCenterX = (imageWidth - oldCropWidth) * crop.position.x + pivot.x * oldCropWidth;
  const newCropWidth = 1 / zoom;
  const overflowX = imageWidth - newCropWidth;
  const x = overflowX > 0 ? (cropCenterX - pivot.x * newCropWidth) / overflowX : 0.5;

  const imageHeight = imageAspect > crop.aspect ? 1 : crop.aspect / imageAspect;
  const oldCropHeight = 1 / crop.zoom;
  const cropCenterY = (imageHeight - oldCropHeight) * crop.position.y + pivot.y * oldCropHeight;
  const newCropHeight = 1 / zoom;
  const overflowY = imageHeight - newCropHeight;
  const y = overflowY > 0 ? (cropCenterY - pivot.y * newCropHeight) / overflowY : 0.5;

  return restrictCrop({
    ...crop,
    position: { x, y },
    zoom,
  });
};

const useDocumentEventListener = (event: keyof DocumentEventMap, handler: (event: MouseEvent) => void) => {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  const handleEvent = useCallback((event: MouseEvent) => {
    handlerRef.current(event);
  }, []);

  useEffect(() => {
    document.addEventListener(event, handleEvent);
    return () => {
      document.removeEventListener(event, handleEvent);
    };
  }, [event, handleEvent]);
};

const Cropper = forwardRef<Handle, Props>(({ className, crop, imageUrl, onCropChange, maxZoom }: Props, ref) => {
  const previewRef = useRef<HTMLDivElement | null>(null);
  const dragStart = useRef<{ pointerPosition: Point; cropPosition: Point } | undefined>(undefined);
  const imageSize = useImageSize(imageUrl);

  useDocumentEventListener('mouseup', () => {
    dragStart.current = undefined;
  });

  useDocumentEventListener('mousemove', event => {
    if (!dragStart.current || !imageSize || !previewRef.current) {
      return;
    }
    const viewportWidth = previewRef.current.clientWidth;
    const viewportHeight = previewRef.current.clientHeight;
    const imageDisplayScale = Math.max(viewportWidth / imageSize.width, viewportHeight / imageSize.height) * crop.zoom;
    const imageDisplayWidth = imageSize.width * imageDisplayScale;
    const imageDisplayHeight = imageSize.height * imageDisplayScale;
    const imageOverflowX = imageDisplayWidth - viewportWidth;
    const imageOverflowY = imageDisplayHeight - viewportHeight;
    onCropChange(
      restrictCrop({
        ...crop,
        position: {
          x:
            imageOverflowX > 0
              ? dragStart.current.cropPosition.x -
                (event.clientX - dragStart.current.pointerPosition.x) / imageOverflowX
              : 0.5,
          y:
            imageOverflowY > 0
              ? dragStart.current.cropPosition.y -
                (event.clientY - dragStart.current.pointerPosition.y) / imageOverflowY
              : 0.5,
        },
      }),
    );
  });

  const zoom = (pivot: { x: number; y: number }, zoom: number) => {
    if (!imageSize) {
      return;
    }
    onCropChange(zoomWithPivot(imageSize.width / imageSize.height, pivot, R.clamp(1, maxZoom, zoom), crop));
  };

  useImperativeHandle(ref, () => ({
    zoom: (newZoom: number) => {
      zoom({ x: 0.5, y: 0.5 }, newZoom);
    },
  }));

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    dragStart.current = { pointerPosition: { x: event.clientX, y: event.clientY }, cropPosition: crop.position };
  };

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (!imageSize || !previewRef.current) {
      return;
    }
    const viewportRect = previewRef.current.getBoundingClientRect();
    zoom(
      {
        x: (event.clientX - viewportRect.left) / viewportRect.width,
        y: (event.clientY - viewportRect.top) / viewportRect.height,
      },
      crop.zoom - WHEEL_SENSITIVITY * event.deltaY,
    );
  };

  return (
    <Preview
      className={className}
      crop={crop}
      imageUrl={imageUrl}
      onMouseDown={handleMouseDown}
      onWheel={handleWheel}
      ref={element => {
        if (previewRef.current) {
          previewRef.current.removeEventListener('wheel', preventDefault);
        }
        if (element) {
          element.addEventListener('wheel', preventDefault, { passive: false });
        }
        previewRef.current = element;
      }}
    />
  );
});

export default Cropper;
